import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheckIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import aaaPdf from '../../../assets/aaa.pdf';
import '../Checkout.css';

export interface PaymentInfo {
  cardNumber: string;
  expiryDate: string;
  cvv: string;
  cardholderName: string;
  saveCard: boolean;
  agreeToTerms: boolean;
}

interface PaymentSectionProps {
  paymentInfo: PaymentInfo;
  errors: { [key: string]: string };
  orderNotes: string;
  onOrderNotesChange: (notes: string) => void;
  onAgreeToTermsChange: (checked: boolean) => void;
  /** Signed Hyp payment page URL (shown in iframe after terms) */
  hypPaymentUrl: string | null;
  hypLoading: boolean;
  hypError: string | null;
}

const PaymentSection: React.FC<PaymentSectionProps> = ({
  paymentInfo,
  errors,
  orderNotes,
  onOrderNotesChange,
  onAgreeToTermsChange,
  hypPaymentUrl,
  hypLoading,
  hypError,
}) => {
  const { t } = useTranslation();
  const [iframeLoaded, setIframeLoaded] = useState(false);

  return (
    <div className="checkout-step">
      <h2>{t('checkout.paymentInformation')}</h2>

      {!hypPaymentUrl && (
        <>
          <div className="form-group">
            <label htmlFor="orderNotes">{t('checkout.orderNotes')}</label>
            <textarea
              id="orderNotes"
              rows={3}
              value={orderNotes}
              onChange={(e) => onOrderNotesChange(e.target.value)}
              placeholder={t('checkout.orderNotesPlaceholder')}
            />
          </div>

          <div className="hyp-payment-notice">
            <LockClosedIcon className="hyp-payment-notice-icon" />
            <p className="hyp-payment-notice-title">
              {t('checkout.hypReadyTitle')}
            </p>
            <p className="hyp-payment-notice-text">
              {t('checkout.hypAgreeToShow')}
            </p>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="agreeToTerms"
                checked={paymentInfo.agreeToTerms}
                onChange={(e) => onAgreeToTermsChange(e.target.checked)}
                className={errors.agreeToTerms ? 'error' : ''}
                disabled={hypLoading}
              />
              <span>
                {t('checkout.agreeToTermsPart1')}{' '}
                <a
                  href={aaaPdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="terms-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('checkout.companyTerms')}
                </a>
              </span>
            </label>
            {errors.agreeToTerms && (
              <span className="error-message">{errors.agreeToTerms}</span>
            )}
          </div>
        </>
      )}

      {hypLoading && (
        <div className="hyp-iframe-loading">
          <p>{t('checkout.hypLoading')}</p>
        </div>
      )}

      {hypError && (
        <div className="error-message general-error">{hypError}</div>
      )}

      {hypPaymentUrl && (
        <div className="hyp-iframe-wrap">
          <p className="hyp-iframe-caption">{t('checkout.hypPayBelow')}</p>
          {!iframeLoaded && (
            <div className="hyp-iframe-loading">
              <p>{t('checkout.hypLoading')}</p>
            </div>
          )}
          <iframe
            title="Hyp Secure Payment"
            src={hypPaymentUrl}
            className="hyp-payment-iframe"
            allow="payment *"
            onLoad={() => setIframeLoaded(true)}
          />
        </div>
      )}

      <div className="security-badges">
        <div className="security-badge">
          <ShieldCheckIcon className="badge-icon" />
          <span>{t('checkout.sslEncrypted')}</span>
        </div>
        <div className="security-badge">
          <LockClosedIcon className="badge-icon" />
          <span>{t('checkout.securePayment')}</span>
        </div>
      </div>
    </div>
  );
};

export default PaymentSection;
